/**
 * Timezone assumed when an organisation hasn't chosen one yet. `Organization.timezone`
 * stays `null` until the owner confirms it, so the completeness checklist can still flag
 * it as unset.
 */
export const DEFAULT_ORG_TIMEZONE = 'Australia/Melbourne';
/** One day in milliseconds — the canonical unit for "N days from/after" date arithmetic. */
export const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
/** Returns the organisation's timezone, falling back to {@link DEFAULT_ORG_TIMEZONE}. */
export function resolveOrgTimezone(timezone) {
    return timezone ?? DEFAULT_ORG_TIMEZONE;
}
/** Returns the `yyyy-MM-dd` calendar date that `instant` falls on in `timezone`. */
export function calendarDateInTimezone(instant, timezone) {
    // sv-SE formats as yyyy-MM-dd
    return new Intl.DateTimeFormat('sv-SE', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(instant);
}
/**
 * Converts a `yyyy-MM-dd` calendar date to the midnight-UTC instant used to store
 * date-only fields (`issueDate`, `dueAt`, `validUntil`).
 */
export function calendarDateToUtcMidnight(calendarDate) {
    return new Date(`${calendarDate}T00:00:00.000Z`);
}
/** Shifts a `yyyy-MM-dd` calendar date by whole days, independent of any timezone. */
export function addCalendarDays(calendarDate, days) {
    return new Date(calendarDateToUtcMidnight(calendarDate).getTime() + days * MILLISECONDS_PER_DAY)
        .toISOString()
        .slice(0, 10);
}
/**
 * Returns today's date in `timezone` as a midnight-UTC instant, ready to store in a
 * date-only field.
 */
export function todayInTimezone(timezone, now = new Date()) {
    return calendarDateToUtcMidnight(calendarDateInTimezone(now, timezone));
}
/**
 * Formats a date-only field (stored as midnight UTC) without shifting it into the
 * viewer's or server's timezone — a zone behind UTC would otherwise render the day
 * before.
 */
export function formatCalendarDate(value, locale, options = { dateStyle: 'long' }) {
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(typeof value === 'string' ? new Date(value) : value);
}
//# sourceMappingURL=dates.js.map